import type { PlayerSummary } from '@brush/api-client';

/** How many players the picker offers at once — what it showed when it searched the roster itself. */
export const PICKER_SHOWN = 8;

export interface PickerState {
  status: 'searching' | 'failed' | 'ready';
  /** The players the operator can pick, in the server's order (by surname). */
  available: PlayerSummary[];
  /** Players matching the term who are already on the sheet, said so rather than left out silently. */
  alreadyAdded: PlayerSummary[];
}

/**
 * What the picker shows, from the server's answer.
 *
 * `pending` is true while the answer on hand is not for what is typed — the box is ahead of the debounce,
 * or the query is still answering the last term. Nothing is offered then: an old list would let the
 * operator pick somebody the new term does not match, and an empty one would read "No player matches
 * that" before the search had finished.
 */
export function pickerState(input: {
  term: string;
  found: readonly PlayerSummary[] | undefined;
  excludeIds: readonly string[];
  pending: boolean;
  failed: boolean;
}): PickerState {
  if (input.failed) return { status: 'failed', available: [], alreadyAdded: [] };
  if (input.pending || input.found === undefined) return { status: 'searching', available: [], alreadyAdded: [] };

  const taken = new Set(input.excludeIds);
  const available = input.found.filter((p) => !taken.has(p.id)).slice(0, PICKER_SHOWN);
  // With nothing typed every player "matches", so naming the sheet's would say nothing worth reading.
  const alreadyAdded = input.term.trim() === '' ? [] : input.found.filter((p) => taken.has(p.id));
  return { status: 'ready', available, alreadyAdded };
}
